import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject } from 'rxjs';
import { CommonDataService } from '../common-data.service';
import { EstimateCommonFunctionsService } from '../estimate/estimate-common-functions.service';
import { LaborAndEscalationChangeDialogComponent } from '../../Components/dialogs/common/informative-dialog/labor-and-escalation-change-dialog.component';
import { ILaborAndEscalationChangeDialogData } from '../../models/common/interfaces/ILaborAndEscalationChangeDialogData';
import {
  CollaborationState,
  EscalationData as EscalationDataDTO,
  Escalations,
  EstimateStatus
} from '../../models/dtos';
import { SolEstimateService } from '../estimate/sol-estimate.service';
import { CONSTANTS } from '../../Constants/Constants';

/**
 * Escalation Reset Service
 *
 * Handles the escalation reset logic during SOL estimate open.
 *
 * Two independent reset flows:
 *
 * A. CRM-driven reset (`checkEscalationReset`):
 *    When CRM relationship data indicates that start/end dates changed (triggering
 *    material or labor escalation date changes), this service:
 *     1. Notifies the user if escalation was previously enabled
 *     2. Resets all escalation start dates to the current fiscal year start (Oct 1)
 *     3. Updates the job start date on all escalation categories
 *     4. Resets only the material/labor types indicated by the CRM reset flag
 *     5. Distributes whole percentages across applicable fiscal years, with the balance in the
 *        first applicable period and special handling for material Q4 and past-start labor
 *
 * B. Financial-year-change reset (`resetEscalationIfOpenedInNewFinancialYear`):
 *    When an estimate saved in a previous financial year is re-opened, this service:
 *     1. Guards against Submitted status and non-editable collaboration states
 *     2. Resets escalation start dates to the current FY start (Oct 1)
 *     3. If the job start FY is also in the past → redistributes percentages across
 *        applicable remaining years
 *     4. If the job start FY is current or future → shifts existing percentages forward
 *        by the number of FYs elapsed since last save
 *     5. For legacy estimates (version ≤ 6.7) → applies the Set44OpEscalations logic
 */

/** Maximum number of escalation years supported */
const MAX_ESCALATION_YEARS = 5;

/** Fiscal year starts in October (month index 9) */
const FISCAL_YEAR_START_MONTH = 9; // October (0-indexed)

/** Material Q4 months: July(6), August(7), September(8) */
const MATERIAL_Q4_MONTHS = new Set<number>([6, 7, 8]);

/** Number of months to shift for fiscal year alignment */
const FISCAL_MONTH_SHIFT = 3;

/** Estimates at or below this version use legacy Set44OpEscalations logic */
const LEGACY_VERSION_THRESHOLD = 6.7;

/** Maximum number of year slots that can be shifted (1–4) */
const MAX_SHIFT_YEARS = 4;

/** Zeroed year-slot distribution */
const EMPTY_PERCENTAGES: readonly number[] = [0, 0, 0, 0, 0];

/** Whole escalation placed in the first year slot */
const FIRST_SLOT_PERCENTAGES: readonly number[] = [100, 0, 0, 0, 0];

enum EscalationType {
  Material = 0,
  Labor = 1
}

/** Project dates reach this service either as Date objects or as serialized strings. */
type DateInput = Date | string | null;

@Injectable({
  providedIn: 'root'
})
export class EscalationResetService {

  /** Emits after escalation data has been reset during estimate open. */
  readonly escalationReset$ = new Subject<void>();

  /**
 * Flag indicating whether the escalation reset was triggered from the review tab.
 */
  private _isEscalationResetFromReview: boolean = false;

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly estimateCommonFunctions: EstimateCommonFunctionsService,
    private readonly solEstimateService: SolEstimateService
  ) { }

  get isEscalationResetFromReview(): boolean {
    return this._isEscalationResetFromReview;
  }

  set isEscalationResetFromReview(value: boolean) {
    this._isEscalationResetFromReview = value;
  }

  /**
   * Main entry point — checks whether escalation reset is needed and performs it.
   *
   * Called during estimate open after CRM relationship data has been loaded.
   * Reads the `isEscalationResetNeeded` flag from OpportunityRelationshipData.
   *
   * @param isArchived Whether the estimate is archived (skip reset if true)
   */
  checkEscalationReset(isArchived: boolean): void {
    // Step 3a: Determine the reset flag
    const flag = this.getEscalationResetFlag(isArchived);

    // Guard — skip if no reset needed
    if (flag !== 'M' && flag !== 'L' && flag !== 'A') {
      return;
    }

    // Step 3b: Determine what changed
    const isMaterialEscChanged = flag === 'M' || flag === 'A';
    const isLaborEscChanged = flag === 'L' || flag === 'A';

    const estimateMetaData = this.commonDataService.getEstimateMetaData();
    const escalations = estimateMetaData?.escalationRates;
    if (!escalations) {
      return;
    }

    // Step 3c: Show informational message if escalation was enabled
    this.showEscalationChangedNotification(escalations, isMaterialEscChanged, isLaborEscChanged);

    const estimateData = this.commonDataService.getEstimateData();
    const jobStartDate = estimateData?.startDate ? new Date(estimateData.startDate) : new Date();
    const jobEndDate = estimateData?.endDate ? new Date(estimateData.endDate) : new Date();
    const now = new Date();

    // Step 3d: Reset all escalation start dates to current fiscal year start
    const fiscalYearStart = this.getFiscalYearStart(now);
    this.setEscalationStartDates(escalations, fiscalYearStart);

    // Step 3e: Set job start date on all escalation categories
    this.setJobStartDate(escalations, jobStartDate);

    // Step 3f: Recalculate and redistribute material escalation percentages
    if (isMaterialEscChanged) {
      this.setEscalationPercentages(escalations, jobStartDate, jobEndDate, EscalationType.Material, now);
    }

    // Step 3g: Recalculate and redistribute labor escalation percentages
    if (isLaborEscChanged) {
      this.setEscalationPercentages(escalations, jobStartDate, jobEndDate, EscalationType.Labor, now);
    }

    // Persist the updated escalation rates back to EstimateMetaData
    estimateMetaData.escalationRates = escalations;
    this.commonDataService.patchEstimateMetaData(estimateMetaData);

    // Step 3h: Notify subscribers that escalation data has been reset
    this.escalationReset$.next();
  }

  // ─── Financial-Year-Change Reset ─────────────────────────────────

  /**
   * Resets escalation data when an estimate is opened in a new financial year
   * compared to when it was last saved.
   *
   * Called during estimate open after metadata and estimate data have been loaded.
   * Implements the desktop `ResetEscalationIfOpenedInNewFinancialYear` logic.
   *
   * @param collaborationState The collaboration state at the time of opening
   */
  resetEscalationIfOpenedInNewFinancialYear(collaborationState: CollaborationState): void {
    // ── Step 1: Guard checks ──────────────────────────────────────
    const estimateData = this.commonDataService.getEstimateData();
    if (!estimateData) {
      return;
    }

    // Submitted estimates are frozen — no escalation reset
    if (estimateData.estimateStatusId === EstimateStatus.Submitted) {
      return;
    }

    // Only NonCollaborative or Edit states allow escalation reset
    if (
      collaborationState !== CollaborationState.NonCollaborative &&
      collaborationState !== CollaborationState.Edit
    ) {
      return;
    }

    const escalationRates = this.commonDataService.getEstimateMetaData()?.escalationRates;
    if (!escalationRates) {
      return;
    }

    // ── Step 2: Gather dates ──────────────────────────────────────
    // creationDate = last-modified date (when the estimate was last saved)
    const creationDate = estimateData.modifiedDate
      ? new Date(estimateData.modifiedDate)
      : new Date();
    const startDate = estimateData.startDate
      ? new Date(estimateData.startDate)
      : new Date();
    const endDate = estimateData.endDate
      ? new Date(estimateData.endDate)
      : new Date();
    const now = new Date();

    // ── Step 3: Financial year determination ──────────────────────
    const creationFY = this.getFiscalYear(creationDate);
    const nowFY = this.getFiscalYear(now);

    let materialReset = false;
    let laborReset = false;

    // ── Steps 4 & 5: FY-change material & labor reset ────────────
    // Condition: estimate was last saved in a previous financial year
    if (creationFY < nowFY) {
      // Reset all escalation start dates to current FY start (Oct 1)
      const fiscalYearStart = this.getFiscalYearStart(now);
      this.setEscalationStartDates(escalationRates, fiscalYearStart);

      // Step 4: Material escalation reset
      materialReset = this.resetEscalationForFYChange(
        escalationRates, startDate, endDate, creationDate, EscalationType.Material, now
      );

      // Step 5: Labor escalation reset (identical logic, applied to labor categories)
      laborReset = this.resetEscalationForFYChange(
        escalationRates, startDate, endDate, creationDate, EscalationType.Labor, now
      );
    }

    // ── Step 6: Result handling ──────────────────────────────────
    if (materialReset || laborReset) {
      // Persist updated escalation rates back to EstimateMetaData
      const estimateMetaData = this.commonDataService.getEstimateMetaData();
      estimateMetaData.escalationRates = escalationRates;
      this.commonDataService.patchEstimateMetaData(estimateMetaData);

      // Inform the user that the financial year has changed and escalation was reset
      this.openEscalationInfoDialog(
        this.translate.instant('ESCALATION_FY_CHANGE_TITLE'),
        this.translate.instant('ESCALATION_FY_CHANGE_RESET_MESSAGE').replaceAll('{0}', '\n'),
        false
      );

      // Notify subscribers that escalation data has been reset
      this.escalationReset$.next();
    }
  }

  // ─── Project-date-change reset ───────────────────────────────────

  /**
   * Determines whether changing the project start/end dates invalidates the current
   * escalation spread.
   *
   * The spread is built per financial year (Oct 1 – Sep 30), so a reset is required when the
   * edited dates no longer sit in the same financial years as the ones the estimate was opened
   * with — which also covers a job that now overlaps two financial years. Material additionally
   * shifts a year when the job starts in the fourth quarter, so crossing that boundary counts
   * too (2 May → 2 Jun is a no-op, 2 May → 2 Jul is not).
   */
  isEscalationResetRequiredForDateChange(
    originalStartDate: DateInput,
    originalEndDate: DateInput,
    newStartDate: DateInput,
    newEndDate: DateInput
  ): boolean {

    const datesInvalidSpread = this.isJobStartBeyondEscalationWindow(newStartDate) ||
      this.hasFiscalYearChanged(originalStartDate, newStartDate) ||
      this.hasFiscalYearChanged(originalEndDate, newEndDate) ||
      this.hasFourthQuarterChanged(originalStartDate, newStartDate);

    if (!datesInvalidSpread) {
      return false;
    }

    const isAnyEscalationEnabled = this.isAnyEscalationEnabled();

    const enabledUnderNewDates = this.isEscalationEnabledUnderDates(newStartDate, newEndDate);

    return isAnyEscalationEnabled || enabledUnderNewDates;
  }

  isEscalationConfirmationRequired(newStartDate: DateInput, newEndDate: DateInput): boolean {
    const isAnyEscalationEnabled = this.isAnyEscalationEnabled();
    const enabledUnderNewDates = this.isEscalationEnabledUnderDates(newStartDate, newEndDate);
    return isAnyEscalationEnabled || !enabledUnderNewDates;
  }

  private isEscalationEnabledUnderDates(startDate: DateInput, endDate: DateInput): boolean {
    if (!startDate || !endDate) {
      return false;
    }
    return this.buildDateChangeSpread(new Date(startDate), new Date(endDate), new Date()).escalationEnabled;
  }

  private isAnyEscalationEnabled(): boolean {
    const escalationRates = this.commonDataService.getEstimateMetaData()?.escalationRates;
    if (!escalationRates) {
      return false;
    }
    return this.estimateCommonFunctions.isMaterialEscalationEnabled(escalationRates) || this.estimateCommonFunctions.isLaborEscalationEnabled(escalationRates);
  }

  /**
   * Asks the user to confirm the escalation reset triggered by a project duration change.
   * Resolves to false when the user cancels or dismisses the dialog.
   */
  async confirmEscalationResetForDateChange(): Promise<boolean> {
    const data: ILaborAndEscalationChangeDialogData = {
      header: this.translate.instant('ESCALATION_CHANGE_TITLE'),
      message: this.translate.instant('ESCALATION_PROJECT_DURATION_CHANGED_MESSAGE'),
      isConfirmative: true
    };

    const dialogRef = this.dialog.open(LaborAndEscalationChangeDialogComponent, {
      width: '520px',
      disableClose: true,
      data
    });

    return (await firstValueFrom(dialogRef.afterClosed())) === true;
  }

  /**
   * Re-spreads material and labor escalation percentages for the new project dates.
   *
   * Year slots are anchored to the current financial year start (Oct 1), so slot N covers
   * FY(now) + N. Percentages are spread evenly, in whole numbers, across every financial
   * year the job touches; a remainder goes to the first populated slot (3 years → 34/33/33).
   * Material skips the first financial year when the job starts in the fourth quarter
   * (Jul–Sep), because no material is bought in that FY.
   *
   * A job that finishes in the current financial year or earlier cannot escalate: everything
   * lands in slot 1 and escalation is switched off.
   *
   * Mutates the supplied rates in place so callers can apply it to staged (unsaved) metadata.
   */
  resetEscalationForDateChange(
    escalationRates: Escalations,
    jobStartDate: DateInput,
    jobEndDate: DateInput
  ): void {
    if (!jobStartDate || !jobEndDate || !escalationRates) {
      return;
    }
    const now = new Date();
    const startDate = new Date(jobStartDate);
    const endDate = new Date(jobEndDate);

    const spread = this.buildDateChangeSpread(startDate, endDate, now);
    const fiscalYearStart = this.getFiscalYearStart(now);

    for (const category of this.getMaterialCategories(escalationRates)) {
      this.applyDateChangeDistribution(category, spread.material, fiscalYearStart, startDate, spread.escalationEnabled);
    }

    for (const category of this.getAllLaborCategoriesFlattened(escalationRates)) {
      this.applyDateChangeDistribution(category, spread.labor, fiscalYearStart, startDate, spread.escalationEnabled);
    }
  }

  /**
   * Picks the material and labor year-slot spreads for the new project dates.
   */
  private buildDateChangeSpread(
    startDate: Date,
    endDate: Date,
    now: Date
  ): { material: readonly number[]; labor: readonly number[]; escalationEnabled: boolean } {
    const currentFiscalYear = this.getFiscalYear(now);
    const firstFiscalYear = Math.min(this.getFiscalYear(startDate), this.getFiscalYear(endDate));
    const lastFiscalYear = Math.max(this.getFiscalYear(startDate), this.getFiscalYear(endDate));

    // The job finishes before escalation starts, or starts after the fifth period ends — either
    // way no cost can ever be escalated.
    if (lastFiscalYear <= currentFiscalYear || firstFiscalYear >= currentFiscalYear + MAX_ESCALATION_YEARS) {
      return { material: FIRST_SLOT_PERCENTAGES, labor: FIRST_SLOT_PERCENTAGES, escalationEnabled: false };
    }

    const startsInFourthQuarter = MATERIAL_Q4_MONTHS.has(startDate.getMonth());
    const firstSlot = Math.max(0, firstFiscalYear - currentFiscalYear);
    const lastSlot = Math.min(MAX_ESCALATION_YEARS - 1, lastFiscalYear - currentFiscalYear);
    const materialFirstSlot = lastSlot > firstSlot && startsInFourthQuarter ? firstSlot + 1 : firstSlot;

    return {
      material: this.buildEvenPercentageDistribution(materialFirstSlot, lastSlot),
      labor: this.buildEvenPercentageDistribution(firstSlot, lastSlot),
      escalationEnabled: true
    };
  }

  /**
   * Spreads 100% evenly over the inclusive slot range using whole percentages, giving the
   * remainder to the first slot.
   */
  private buildEvenPercentageDistribution(firstSlot: number, lastSlot: number): number[] {
    const percentages = [...EMPTY_PERCENTAGES];
    const slotCount = lastSlot - firstSlot + 1;

    if (slotCount <= 0) {
      return percentages;
    }

    const basePercentage = Math.floor(100 / slotCount);
    for (let slot = firstSlot; slot <= lastSlot; slot++) {
      percentages[slot] = basePercentage;
    }
    percentages[firstSlot] += 100 - basePercentage * slotCount;

    return percentages;
  }

  /**
   * Applies a date-change distribution to one category.
   */
  private applyDateChangeDistribution(
    category: EscalationDataDTO,
    percentages: readonly number[],
    fiscalYearStart: Date,
    jobStartDate: Date,
    escalationEnabled: boolean
  ): void {
    category.escalationStartDate = new Date(fiscalYearStart);
    category.estimateStartDate = new Date(jobStartDate);
    category.escalationEnabled = escalationEnabled;
    category.firstYearPercentage = percentages[0];
    category.secondYearPercentage = percentages[1];
    category.thirdYearPercentage = percentages[2];
    category.fourthYearPercentage = percentages[3];
    category.fifthYearPercentage = percentages[4];
    category.escalatedCost = this.estimateCommonFunctions.calculateTotalEscalatedCost(category);
  }

  /**
   * Lets callers that reset escalation data announce it once the data has been committed.
   *
   * The cost + sell-price pipeline runs here rather than in the estimate component, so the new
   * percentages are applied even when the estimate tab isn't mounted (properties panel, review
   * tab). Subscribers only have to repaint.
   */
  notifyEscalationReset(): void {
    this.solEstimateService.recalculateFromEscalationReset();
    this.escalationReset$.next();
  }

  notifyEscalationResetWithoutPatch(): void {
    this.solEstimateService.recalculateFromEscalationReset();
  }

  /**
   * True when the date moved into a different financial year. A missing date on either side
   * counts as a change only when the other side has a value.
   */
  private hasFiscalYearChanged(originalDate: DateInput, newDate: DateInput): boolean {
    if (!originalDate || !newDate) {
      return !!originalDate !== !!newDate;
    }

    return this.getFiscalYear(new Date(originalDate)) !== this.getFiscalYear(new Date(newDate));
  }

  /**
   * The five year slots only cover the current financial year and the four that follow it, so a
   * job starting after the fifth period ends (30 Sep of FY(now) + 5) can never be escalated.
   */
  isJobStartBeyondEscalationWindow(startDate: DateInput): boolean {
    if (!startDate) {
      return false;
    }

    return this.getFiscalYear(new Date(startDate)) >= this.getFiscalYear(new Date()) + MAX_ESCALATION_YEARS;
  }

  /**
   * True when the date moved in or out of the fourth quarter (Jul–Sep). Missing dates are
   * already reported as a change by the financial-year check.
   */
  private hasFourthQuarterChanged(originalDate: DateInput, newDate: DateInput): boolean {
    if (!originalDate || !newDate) {
      return false;
    }

    return (
      MATERIAL_Q4_MONTHS.has(new Date(originalDate).getMonth()) !==
      MATERIAL_Q4_MONTHS.has(new Date(newDate).getMonth())
    );
  }

  // ─── Private helpers ──────────────────────────────────────────────

  // ─── FY-change reset helpers ──────────────────────────────────────

  /**
   * Performs the FY-change escalation reset for a specific escalation type
   * (material or labor).
   *
   * Two branches:
   *  A. Job start FY is in the past → reset all percentages to 0, then redistribute
   *     evenly across the remaining applicable years (from now to job end).
   *  B. Job start FY is current or future → shift existing percentages forward by
   *     the number of FYs elapsed since last save.
   *
   * @returns true if any category of the given type has escalation enabled
   */
  private resetEscalationForFYChange(
    escalationRates: Escalations,
    startDate: Date,
    endDate: Date,
    creationDate: Date,
    escalationType: EscalationType,
    now: Date
  ): boolean {
    const startDateFY = this.getFiscalYear(startDate);
    const nowFY = this.getFiscalYear(now);
    const creationFY = this.getFiscalYear(creationDate);

    const categories = escalationType === EscalationType.Material
      ? this.getMaterialCategories(escalationRates)
      : this.getAllLaborCategoriesFlattened(escalationRates);

    if (startDateFY < nowFY) {
      // Branch A: Job started in a past FY — reset and redistribute
      for (const category of categories) {
        if (category?.escalationEnabled) {
          this.resetAndRedistributePercentages(category, now, endDate);
        }
      }
    } else {
      // Branch B: Job hasn't started yet or starts in current FY — shift backward
      const yearsFromCreation = nowFY - creationFY;
      if (yearsFromCreation >= 1 && yearsFromCreation <= MAX_SHIFT_YEARS) {
        for (const category of categories) {
          if (category?.escalationEnabled) {
            this.shiftPercentagesBackward(category, yearsFromCreation);
          }
        }
      }
      // If yearsFromCreation > 4, no action (default case)
    }

    // Return whether any category of this type has escalation enabled
    return escalationType === EscalationType.Material
      ? this.estimateCommonFunctions.isMaterialEscalationEnabled(escalationRates)
      : this.estimateCommonFunctions.isLaborEscalationEnabled(escalationRates);
  }

  /**
   * Resets all percentages to 0, then redistributes evenly across the
   * remaining applicable fiscal years (from now to job end).
   * Also recalculates the escalated cost for the category.
   */
  private resetAndRedistributePercentages(
    category: EscalationDataDTO,
    now: Date,
    endDate: Date
  ): void {
    // 1. Reset all escalation percentages to 0
    category.firstYearPercentage = 0;
    category.secondYearPercentage = 0;
    category.thirdYearPercentage = 0;
    category.fourthYearPercentage = 0;
    category.fifthYearPercentage = 0;

    // 2. Determine year-wise escalation applicability
    const applicableYears = Math.min(
      this.getFiscalYearsSpanned(now, endDate),
      MAX_ESCALATION_YEARS
    );

    // 3. Set percentages per validity
    if (applicableYears > 0) {
      const percentagePerYear = 100 / applicableYears;
      const percentages = this.buildPercentageDistribution(0, applicableYears, percentagePerYear);
      category.firstYearPercentage = percentages[0];
      category.secondYearPercentage = percentages[1];
      category.thirdYearPercentage = percentages[2];
      category.fourthYearPercentage = percentages[3];
      category.fifthYearPercentage = percentages[4];
    }

    // 4. Recalculate escalated cost
    category.escalatedCost = this.estimateCommonFunctions.calculateTotalEscalatedCost(category);
  }

  /**
   * Shifts existing escalation percentages backward by the given number of year slots.
   *
   * E.g., shift by 1: [Y1, Y2, Y3, Y4, Y5] → [Y2, Y3, Y4, Y5, 0]
   *        shift by 2: [Y1, Y2, Y3, Y4, Y5] → [Y3, Y4, Y5, 0, 0]
   *
   * Percentages that fall off the first year slot are dropped.
   */
  private shiftPercentagesBackward(category: EscalationDataDTO, shiftBy: number): void {
    const current = [
      category.firstYearPercentage,
      category.secondYearPercentage,
      category.thirdYearPercentage,
      category.fourthYearPercentage,
      category.fifthYearPercentage
    ];

    const shifted = [0, 0, 0, 0, 0];
    for (let i = 0; i < MAX_ESCALATION_YEARS; i++) {
      const newIndex = i - shiftBy;
      if (newIndex >= 0) {
        shifted[newIndex] = current[i];
      }
    }

    category.firstYearPercentage = shifted[0];
    category.secondYearPercentage = shifted[1];
    category.thirdYearPercentage = shifted[2];
    category.fourthYearPercentage = shifted[3];
    category.fifthYearPercentage = shifted[4];
  }

  /**
   * Returns all labor escalation categories (fixed + additional) as a flat array.
   */
  private getAllLaborCategoriesFlattened(escalationRates: Escalations): EscalationDataDTO[] {
    const fixedLabor = this.getLaborCategories(escalationRates);
    const additionalLabor: EscalationDataDTO[] = [];

    if (escalationRates.additionalLaborCatEscalationsList?.length > 0) {
      for (const item of escalationRates.additionalLaborCatEscalationsList) {
        if (item?.value) {
          additionalLabor.push(item.value);
        }
      }
    }

    return [...fixedLabor, ...additionalLabor];
  }

  // ─── CRM-reset helpers ───────────────────────────────────────────

  /**
   * Reads the CRM escalation reset flag from relationship data.
   * Forces '\0' for archived estimates.
   */
  private getEscalationResetFlag(isArchived: boolean): string | null {
    if (isArchived) {
      return '\0';
    }

    const opportunityData = this.commonDataService.getOpportunityRelationshipData();
    return opportunityData?.isEscalationResetNeeded ?? '\0';
  }

  /**
   * Shows an informational dialog if an affected escalation type was previously enabled.
   */
  private showEscalationChangedNotification(
    escalationRates: Escalations,
    isMaterialEscChanged: boolean,
    isLaborEscChanged: boolean
  ): void {
    const isEscalationEnabled =
      (isMaterialEscChanged && this.estimateCommonFunctions.isMaterialEscalationEnabled(escalationRates)) ||
      (isLaborEscChanged && this.estimateCommonFunctions.isLaborEscalationEnabled(escalationRates));

    if (isEscalationEnabled) {
      this.openEscalationInfoDialog(
        this.translate.instant('ESCALATION_RATE_TITLE'),
        this.translate.instant('ESCALATION_DATES_CHANGED_MESSAGE')
      );
    }
  }

  /**
   * Opens an informational (confirmative) dialog to notify the user
   * about escalation changes, using the same dialog component as sync.
   */
  private openEscalationInfoDialog(header: string, message: string, isConfirmative = true): void {
    const data: ILaborAndEscalationChangeDialogData = {
      header,
      message,
      isConfirmative
    };

    this.dialog.open(LaborAndEscalationChangeDialogComponent, {
      width: '520px',
      disableClose: true,
      data
    });
  }

  /**
   * Calculates the fiscal year start date based on the given date.
   * Fiscal year runs Oct 1 → Sep 30.
   *  - If month > September (Oct–Dec): fiscal year start = Oct 1 of current year
   *  - If month ≤ September (Jan–Sep): fiscal year start = Oct 1 of previous year
   */
  private getFiscalYearStart(date: Date): Date {
    const month = date.getMonth(); // 0-indexed
    const year = date.getFullYear();

    if (month >= FISCAL_YEAR_START_MONTH) {
      // Oct-Dec → FY starts Oct 1 of this year
      return new Date(year, FISCAL_YEAR_START_MONTH, 1);
    } else {
      // Jan-Sep → FY starts Oct 1 of previous year
      return new Date(year - 1, FISCAL_YEAR_START_MONTH, 1);
    }
  }

  /**
   * Returns the fiscal year number for a given date.
   * The fiscal year is the calendar year in which Oct 1 falls.
   * e.g. Oct 2025 → FY 2025, Mar 2026 → FY 2025
   */
  private getFiscalYear(date: Date): number {
    const month = date.getMonth();
    return month >= FISCAL_YEAR_START_MONTH ? date.getFullYear() : date.getFullYear() - 1;
  }

  /**
   * Calculates fiscal years spanned by shifting dates 3 months forward
   * (since FY starts Oct 1, shifting by 3 aligns to calendar year boundaries).
   */
  private getFiscalYearsSpanned(startDate: Date, endDate: Date): number {
    const shiftedStart = new Date(startDate);
    shiftedStart.setMonth(shiftedStart.getMonth() + FISCAL_MONTH_SHIFT);

    const shiftedEnd = new Date(endDate);
    shiftedEnd.setMonth(shiftedEnd.getMonth() + FISCAL_MONTH_SHIFT);

    return Math.abs(shiftedEnd.getFullYear() - shiftedStart.getFullYear()) + 1;
  }

  // ─── Set dates on all categories ──────────────────────────────────

  /**
   * Sets escalationStartDate on every escalation category to the given date.
   */
  private setEscalationStartDates(escalationRates: Escalations, fiscalYearStart: Date): void {
    this.forEachEscalationCategory(escalationRates, (esc) => {
      esc.escalationStartDate = new Date(fiscalYearStart);
    });
  }

  /**
   * Sets estimateStartDate (job start date) on every escalation category.
   */
  private setJobStartDate(escalationRates: Escalations, jobStartDate: Date): void {
    this.forEachEscalationCategory(escalationRates, (esc) => {
      esc.estimateStartDate = new Date(jobStartDate);
    });
  }

  // ─── Percentage redistribution (Set44OpEscalations) ───────────────

  /**
   * Recalculates and redistributes escalation percentages across fiscal years.
   * Implements the Set44OpEscalations logic from the desktop application.
   */
  private setEscalationPercentages(
    escalationRates: Escalations,
    jobStartDate: Date,
    jobEndDate: Date,
    escalationType: EscalationType,
    now: Date
  ): void {
    // Calculate escalation years = max of (FY spanned by Job→JobEnd) and (FY spanned by Now→JobEnd)
    const jobStartFY = this.getFiscalYear(jobStartDate);
    const jobEndFY = this.getFiscalYear(jobEndDate);
    const nowFY = this.getFiscalYear(now);

    let escalationYears = Math.max(jobEndFY - jobStartFY + CONSTANTS.ONE, jobEndFY - nowFY + CONSTANTS.ONE);

    // Calculate start year = max of (FY distance from JobStart to Now) and (FY distance from Now to JobStart)
    let startYear = Math.max(nowFY - jobStartFY + CONSTANTS.ONE, jobStartFY - nowFY + CONSTANTS.ONE);

    // Material-specific: Fourth-quarter logic
    if (escalationType === EscalationType.Material) {
      const jobStartMonth = jobStartDate.getMonth();
      if (MATERIAL_Q4_MONTHS.has(jobStartMonth) && jobEndFY > jobStartFY) {
        startYear += CONSTANTS.ONE;
      }
    }

    // Labor-specific: If job start FY is earlier than current FY, shift the window forward
    if (escalationType === EscalationType.Labor) {
      if (jobStartFY < nowFY) {
        const fyDifference = nowFY - jobStartFY;
        startYear -= fyDifference;
        escalationYears -= fyDifference;
      }
    }

    // Cap at MAX_ESCALATION_YEARS
    const startWithinWindow = startYear <= MAX_ESCALATION_YEARS;
    startYear = Math.min(startYear, MAX_ESCALATION_YEARS);
    escalationYears = Math.min(escalationYears, MAX_ESCALATION_YEARS);
    escalationYears = Math.min(
      MAX_ESCALATION_YEARS - startYear + CONSTANTS.ONE,
      escalationYears - startYear + CONSTANTS.ONE
    );

    // Determine if escalation should be applied (job spans more than 1 FY or starts after year 1)
    const shouldApplyEscalation = startWithinWindow && (escalationYears > CONSTANTS.ONE || startYear > CONSTANTS.ONE);

    // Calculate even distribution
    const escalationPercentage = shouldApplyEscalation && escalationYears > CONSTANTS.ZERO
      ? Math.floor(CONSTANTS.HUNDRED / escalationYears)
      : CONSTANTS.ZERO;

    // Desktop startYear is 1-based; the shared array builder accepts a zero-based index.
    const percentages = this.buildPercentageDistribution(startYear - CONSTANTS.ONE, escalationYears, escalationPercentage);

    // Reset escalation start dates to now (fiscal year start of now)
    const fiscalYearStart = this.getFiscalYearStart(now);

    // Apply to the appropriate categories
    if (escalationType === EscalationType.Material) {
      this.applyToMaterialCategories(escalationRates, percentages, shouldApplyEscalation, fiscalYearStart);
    } else {
      this.applyToLaborCategories(escalationRates, percentages, shouldApplyEscalation, fiscalYearStart);
    }
  }

  /**
   * Builds a spread from a zero-based start index, assigning the balance to its first slot.
   */
  private buildPercentageDistribution(
    startIndex: number,
    escalationYears: number,
    percentagePerYear: number
  ): number[] {
    const percentages = [CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO];

    if (percentagePerYear <= CONSTANTS.ZERO) {
      return percentages;
    }

    let remainingPercentage = CONSTANTS.HUNDRED;
    for (let i = startIndex + CONSTANTS.ONE; i < MAX_ESCALATION_YEARS && i < startIndex + escalationYears; i++) {
      percentages[i] = percentagePerYear;
      remainingPercentage -= percentagePerYear;
    }
    percentages[startIndex] = remainingPercentage;

    return percentages;
  }

  /**
   * Applies percentage distribution and escalation flags to all material categories.
   */
  private applyToMaterialCategories(
    escalationRates: Escalations,
    percentages: number[],
    escalationEnabled: boolean,
    fiscalYearStart: Date
  ): void {
    const materialCategories = this.getMaterialCategories(escalationRates);
    for (const category of materialCategories) {
      if (category) {
        this.applyPercentagesToCategory(category, percentages, escalationEnabled, fiscalYearStart);
      }
    }
  }

  /**
   * Applies percentage distribution and escalation flags to all labor categories.
   */
  private applyToLaborCategories(
    escalationRates: Escalations,
    percentages: number[],
    escalationEnabled: boolean,
    fiscalYearStart: Date
  ): void {
    const laborCategories = this.getLaborCategories(escalationRates);
    for (const category of laborCategories) {
      if (category) {
        this.applyPercentagesToCategory(category, percentages, escalationEnabled, fiscalYearStart);
      }
    }

    // Also apply to additional labor categories
    if (escalationRates.additionalLaborCatEscalationsList?.length > 0) {
      for (const item of escalationRates.additionalLaborCatEscalationsList) {
        if (item?.value) {
          this.applyPercentagesToCategory(item.value, percentages, escalationEnabled, fiscalYearStart);
        }
      }
    }
  }

  /**
   * Applies the calculated percentage distribution to a single escalation category.
   */
  private applyPercentagesToCategory(
    category: EscalationDataDTO,
    percentages: number[],
    escalationEnabled: boolean,
    fiscalYearStart: Date
  ): void {
    category.escalationStartDate = new Date(fiscalYearStart);
    category.escalationEnabled = escalationEnabled;
    category.firstYearPercentage = percentages[0];
    category.secondYearPercentage = percentages[1];
    category.thirdYearPercentage = percentages[2];
    category.fourthYearPercentage = percentages[3];
    category.fifthYearPercentage = percentages[4];
  }

  // ─── Category accessors ───────────────────────────────────────────

  /**
   * Returns all 5 material escalation category references.
   */
  private getMaterialCategories(escalationRates: Escalations): EscalationDataDTO[] {
    return [
      escalationRates.siemensMaterialEscalation,
      escalationRates.siemensAtMaterialEscalation,
      escalationRates.outsideMaterialEscalation,
      escalationRates.mechanicalMaterialEscalation,
      escalationRates.electricalMaterialEscalation
    ];
  }

  /**
   * Returns all 13 fixed labor escalation category references.
   */
  private getLaborCategories(escalationRates: Escalations): EscalationDataDTO[] {
    return [
      escalationRates.pmLaborEscalation,
      escalationRates.elecLaborEscalation,
      escalationRates.specLaborEscalation,
      escalationRates.engLaborEscalation,
      escalationRates.nengLaborEscalation,
      escalationRates.qengLaborEscalation,
      escalationRates.cengLaborEscalation,
      escalationRates.eengLaborEscalation,
      escalationRates.lengLaborEscalation,
      escalationRates.mechLaborEscalation,
      escalationRates.progLaborEscalation,
      escalationRates.grapLaborEscalation,
      escalationRates.ddepLaborEscalation
    ];
  }

  /**
   * Iterates over ALL escalation categories (material + labor + additional labor)
   * and applies a callback to each.
   */
  private forEachEscalationCategory(
    escalationRates: Escalations,
    callback: (esc: EscalationDataDTO) => void
  ): void {
    // Material categories
    for (const category of this.getMaterialCategories(escalationRates)) {
      if (category) {
        callback(category);
      }
    }

    // Fixed labor categories
    for (const category of this.getLaborCategories(escalationRates)) {
      if (category) {
        callback(category);
      }
    }

    // Additional labor categories
    if (escalationRates.additionalLaborCatEscalationsList?.length > 0) {
      for (const item of escalationRates.additionalLaborCatEscalationsList) {
        if (item?.value) {
          callback(item.value);
        }
      }
    }
  }
}
